import { useEffect, useState } from "react";

import AppShell from "../components/AppShell";
import { CardSkeleton } from "../components/Skeleton";
import { getFixedAddresses, type AddressDevice, type FixedAddresses } from "../api/fixedAddresses";

export default function FixedAddressesPage() {
  const [data, setData] = useState<FixedAddresses | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getFixedAddresses()
      .then((d) => alive && setData(d))
      .catch(() => alive && setError("Couldn't load the addresses. Please try again."));
    return () => {
      alive = false;
    };
  }, []);

  async function copy(text: string, key: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied((k) => (k === key ? null : k)), 1500);
    } catch {
      /* clipboard blocked: the text is on screen to type */
    }
  }

  const devices = data?.devices ?? [];
  const changed = devices.filter((d) => d.changed_recently);

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <h1 className="text-2xl font-semibold text-slate-900">Fixed addresses</h1>
        <p className="mt-1 text-slate-600">
          Your router gives each computer and printer a network address, and it can give a different one later. Then
          computers have to look for each other again (KraetdNet does this by itself, but it can take a few seconds).
          Tell the router to always give each one the same address and that never happens.
        </p>

        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
          <p className="font-medium text-slate-900">How to do it (about 10 minutes, once)</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            <li>
              Open your router&rsquo;s settings page in a browser
              {data?.router ? (
                <>
                  : <span className="font-mono">http://{data.router}</span>
                </>
              ) : null}{" "}
              (the router&rsquo;s sticker shows the login).
            </li>
            <li>
              Find <span className="font-medium">DHCP reservation</span> (it may be called Address reservation, Static
              lease, or Bind IP to MAC, usually under LAN or DHCP).
            </li>
            <li>For each item below, add its hardware address (MAC) and the address next to it, then save.</li>
            <li>Restart the router if it asks. Nothing else to change on the computers or printers.</li>
          </ol>
        </div>

        {error ? (
          <p role="alert" className="mt-6 rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        ) : null}
        {data === null && !error ? (
          <div className="mt-6">
            <CardSkeleton />
          </div>
        ) : null}

        {data && changed.length > 0 ? (
          <p className="mt-6 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" role="status">
            {changed.length === 1 ? "1 item has" : `${changed.length} items have`} changed address in the last 30 days.
            Reserving them fixes that.
          </p>
        ) : null}

        {data && devices.length === 0 ? (
          <div className="mt-6 rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-600">
            No computers have reported their network yet. They do within a minute of being switched on.
          </div>
        ) : null}

        {data && devices.length > 0 ? (
          <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-2 font-medium">Computer or printer</th>
                  <th className="px-4 py-2 font-medium">Hardware address (MAC)</th>
                  <th className="px-4 py-2 font-medium">Address to reserve</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={`${d.kind}-${d.id}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">
                        {d.name}
                        <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          {d.kind === "computer" ? "Computer" : "Printer"}
                        </span>
                      </p>
                      {d.connection ? <p className="text-xs text-slate-500">{d.connection}</p> : null}
                    </td>
                    <td className="px-4 py-3">
                      <CopyValue value={d.mac} label="hardware address" copyKey={`mac-${d.id}`} copied={copied} onCopy={copy} />
                    </td>
                    <td className="px-4 py-3">
                      <CopyValue value={d.ip} label="address" copyKey={`ip-${d.id}`} copied={copied} onCopy={copy} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusLine d={d} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {data ? (
          <p className="mt-4 text-xs text-slate-500">
            Printers plugged into a computer with a USB cable aren&rsquo;t listed: they use that computer&rsquo;s
            address. Network printers can usually also be given a fixed address from their own control panel.
          </p>
        ) : null}
      </div>
    </AppShell>
  );
}

function CopyValue({
  value,
  label,
  copyKey,
  copied,
  onCopy,
}: {
  value: string;
  label: string;
  copyKey: string;
  copied: string | null;
  onCopy: (text: string, key: string) => void;
}) {
  if (!value) return <span className="text-slate-400">Not known yet</span>;
  return (
    <button
      type="button"
      onClick={() => onCopy(value, copyKey)}
      className="rounded px-1 font-mono text-slate-800 [-webkit-tap-highlight-color:transparent] hover:bg-slate-100"
      aria-label={`Copy ${label} ${value}`}
      title="Click to copy"
    >
      {value}
      <span className="ml-2 font-sans text-xs text-slate-500">{copied === copyKey ? "Copied" : "Copy"}</span>
    </button>
  );
}

function StatusLine({ d }: { d: AddressDevice }) {
  if (!d.reported) return <span className="text-slate-500">Waiting for this computer to report</span>;
  if (d.assigned_by_router === false)
    return <span className="text-green-700">Already fixed on the computer itself</span>;
  if (d.changed_recently) return <span className="text-amber-700">Changed recently: reserve it</span>;
  if (!d.mac) return <span className="text-slate-500">Hardware address shows up after its first use</span>;
  return <span className="text-slate-600">Reserve it in the router</span>;
}
